import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const UserDetails = () => {
  const { uniqueNumber } = useParams(); // Get uniqueNumber from URL
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}jobseeker/user/${uniqueNumber}`)
      .then((response) => {
        setUser(response.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err)
        setError('Failed to load user data');
        setLoading(false);
      });
  }, [uniqueNumber]);

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const ageDifMs = Date.now() - birthDate.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-center p-4 text-red-500">{error}</div>;
  if (!user) return <div className="text-center p-4">No user data available</div>;

  const age = calculateAge(user.dateOfBirth);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-blue-600 text-center mb-4">
          {user.firstName} {user.lastName} - EJOB Profile
        </h2>
        <div className="flex justify-center mb-4">
          <img
            src={user.avatar || 'https://via.placeholder.com/150'}
            alt="Profile"
            className="w-32 h-32 rounded-full border-2 border-blue-500 object-cover"
          />
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <span className="font-bold">First Name:</span> <span>{user.firstName}</span>
            </div>
            <div>
              <span className="font-bold">Last Name:</span> <span>{user.lastName}</span>
            </div>
            <div>
              <span className="font-bold">Middle Name:</span> <span>{user.middleName || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Email:</span> <span>{user.email}</span>
            </div>
            <div>
              <span className="font-bold">Date of Birth:</span> <span>{user.dateOfBirth || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Age:</span> <span>{age}</span>
            </div>
            <div>
              <span className="font-bold">Gender:</span> <span>{user.gender || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Marital Status:</span> <span>{user.maritalStatus || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <span className="font-bold">Phone Number:</span> <span>{user.phoneNumber || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">WhatsApp Number:</span> <span>{user.whatsappNumber || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Address:</span> <span>{user.address || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Address 2:</span> <span>{user.address2 || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">State:</span> <span>{user.state || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Local Government:</span> <span>{user.localGovernment || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Country:</span> <span>{user.country || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800">Professional Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <span className="font-bold">Profession:</span> <span>{user.job || user.handworkSkill || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Education Level:</span> <span>{user.educationLevel || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Job Mode:</span> <span>{user.jobMode || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Job Decision:</span> <span>{user.jobDecision || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Minimum Salary:</span> <span>{user.minimumSalary || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">TIN:</span> <span>{user.TIN || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Unique Number:</span> <span>{user.uniqueNumber}</span>
            </div>
            <div>
              <span className="font-bold">Date Created:</span>{' '}
              <span>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800">Additional Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <span className="font-bold">Race:</span> <span>{user.race || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Disability:</span> <span>{user.dissability || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Live Alone:</span> <span>{user.liveAlone || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Who Do You Live With:</span> <span>{user.whoDoYouLiveWith || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Any Child:</span> <span>{user.anyChild || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Number of Children:</span> <span>{user.numberOfChildren || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">NIN:</span> <span>{user.nin || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">NIN Verified:</span> <span>{user.isNINVerified ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800">Social Media</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <span className="font-bold">Facebook:</span> <span>{user.facebookHandle || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">Instagram:</span> <span>{user.instagramHandle || 'N/A'}</span>
            </div>
            <div>
              <span className="font-bold">TikTok:</span> <span>{user.tiktokHandle || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;